import { useEffect, type RefObject } from 'react'

declare global {
  interface Window {
    __reportReady?: boolean
  }
}

const READY_ATTRIBUTE = 'data-report-ready'
const PENDING_CHART = '[data-chart-state="pending"]'
const POLL_MS = 50

const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Baskı "hazır" sinyali (docs/REPORT-DOCUMENTS-PLAN.md §7.4): yazı tipleri yüklendi ve her
 * grafik çizimini bitirdi. Sonra `window.__reportReady = true` ve `<html data-report-ready>`;
 * Playwright, Gotenberg (`waitForExpression`) ve "Yazdır" bunu bekler. Kopya verisi tek
 * istekle geldiği için Cube sorgusu beklenmez.
 */
export function useReportReady(rootRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    let cancelled = false

    async function waitUntilSettled(element: HTMLElement) {
      // jsdom'da `document.fonts` yoktur.
      if ('fonts' in document) await document.fonts.ready
      // Düğüm görünümleri (grafikler) editör bağlandıktan sonraki karelerde kurulur.
      await nextFrame()
      await nextFrame()
      while (!cancelled && element.querySelector(PENDING_CHART)) await sleep(POLL_MS)
    }

    void waitUntilSettled(root).then(() => {
      if (cancelled) return
      window.__reportReady = true
      document.documentElement.setAttribute(READY_ATTRIBUTE, '')
    })

    return () => {
      cancelled = true
      window.__reportReady = false
      document.documentElement.removeAttribute(READY_ATTRIBUTE)
    }
  }, [rootRef])
}
